"use client";

import * as React from "react";
import { motion } from "motion/react";
import { Stethoscope, Shield, Building2, Crown, Compass, ArrowRight } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const PHASES = [
  { phase: "01", years: "Origin", title: "The Physician", icon: Stethoscope, body: "MBBS with a specialization in Gynaecology, Delhi. Clinical training instilled the operating discipline that would define everything after: evidence over intuition, protocol under pressure, accountability for outcomes, and the composure required when the room reads the most senior person in it.", gradient: "from-red/30 to-red/0", accent: "text-red", iconBg: "bg-gradient-sovereign" },
  { phase: "02", years: "Foundation", title: "Information Privacy", icon: Shield, body: "The pivot from clinical practice into information privacy and secure infrastructure for governments. Encrypted networks, data sovereignty, mission-critical security built for sovereign-level clients. The foundation of trust, earned by protecting the most sensitive information on earth.", gradient: "from-purple/30 to-purple/0", accent: "text-purple", iconBg: "bg-gradient-royal" },
  { phase: "03", years: "Decade", title: "The Decade of Building", icon: Building2, body: "Co-building and running CryptoMize and MaxiMize Infinium across 18 countries. Scaling from a security-focused consultancy into a Digital Conglomerate. One organization, sustained and grown for a decade, through every global disruption — a period when most organizations contracted.", gradient: "from-emerald/30 to-emerald/0", accent: "text-emerald", iconBg: "bg-gradient-success" },
  { phase: "04", years: "Now", title: "The Operator", icon: Crown, body: "The current form: Chief Operating Officer of a multi-country technology conglomerate. Governance programs, sovereign-scale operational campaigns, and operational coordination across continents. Teams built to be 70% women — not by quota, but by the operating results they produce.", gradient: "from-amber-gold/30 to-amber-gold/0", accent: "text-amber-gold", iconBg: "bg-gradient-premium" },
];

export function GenesisSection() {
  return (
    <Section id="genesis" ariaLabel="The Genesis" background="muted" pattern>
      <SectionHeader
        icon={Compass}
        overline="The Genesis"
        overlineClassName="text-amber-gold"
        pillClassName="glass-tinted-amber-gold border-amber-gold/20"
        title={
          <>
            From Medicine to <span className="text-gradient-premium">Sovereign-Scale Operations.</span>
          </>
        }
        subtitle="The journey of Dr. Jyoti Kush — four distinct phases, each forging the operating discipline of the next."
      />

      <div className="space-y-6 max-w-4xl mx-auto">
        {PHASES.map((p, i) => (
          <motion.div
            key={p.phase}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className={`relative glass-strong rounded-2xl p-6 md:p-7 bg-gradient-to-br ${p.gradient} border border-current/20 group hover:scale-[1.01] transition-all`}
          >
            <div className="grid md:grid-cols-[auto_1fr] gap-5 items-start">
              <div className="flex flex-col items-center">
                <div className={`size-14 rounded-xl ${p.iconBg} flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  <p.icon className="size-7 text-white" />
                </div>
                <div className={`mt-3 text-display-sm leading-none font-bold ${p.accent} opacity-50`}>{p.phase}</div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-overline ${p.accent}`}>Phase {p.phase}</span>
                  <span className="text-muted-foreground opacity-30">·</span>
                  <span className="text-caption text-muted-foreground">{p.years}</span>
                </div>
                <h3 className="text-heading-lg mb-2">{p.title}</h3>
                <p className="text-body-md text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { cls: "text-lime", metric: "10+", label: "Years building" },
          { cls: "text-indigo", metric: "18", label: "Countries" },
          { cls: "text-denim", metric: "0", label: "Public failures" },
          { cls: "text-emerald", metric: "60+", label: "Services" },
        ].map((s) => (
          <div key={s.label} className="glass rounded-xl p-4 text-center">
            <div className={`text-display-sm ${s.cls} font-bold`}>{s.metric}</div>
            <div className="text-caption text-muted-foreground mt-1">{s.label}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}
